/**
 * Carrying an affiliate referral across the membership app.
 *
 * ── The hole this fills ─────────────────────────────────────────────────────
 * A member who shares one of their links hands somebody a URL shaped
 * `…/some-page?ai=<affiliate>&pi=<product>`. On a FlexiFunnels sales page
 * `ft-control.js` reads those two parameters and writes the cookies the
 * checkout later reads. This app loads none of those scripts — not
 * ft-control, not ft-checkout — so a link landing HERE wrote nothing at all.
 *
 * Then the member presses Buy, which is `window.open(sales_url)`: a new tab,
 * usually a different host, with a bare URL. Both halves of the referral were
 * gone before the buyer ever reached a page that could have recorded it, and
 * the sale was credited to nobody. No error, no warning — the affiliate simply
 * never got paid, which is how this reached us as "commission not received"
 * rather than as a broken link.
 *
 * ── What this does ──────────────────────────────────────────────────────────
 * Remembers the two parameters when the app is opened with them, and puts them
 * back on every outbound link to somewhere that can record them. That is all:
 * it does not decide who earns anything. The sales page still writes the
 * cookies, the payment lambda still applies the vendor's cookie mode and
 * expiry window, and this never overrides a referral already in the URL.
 *
 * ── Why sessionStorage ──────────────────────────────────────────────────────
 * The referral lasts for the browsing session in which the link was followed,
 * and no longer. `localStorage` would mean a member who followed a shared link
 * once still credited that affiliate for a purchase made from this app weeks
 * later, from this app's own navigation — attribution we would be inventing
 * rather than observing. The real attribution window belongs to the sales
 * domain's cookies, where the vendor's settings apply.
 */

const KEY = "ff_membership_referral";

/** Read what the app was opened with, if anything. */
function fromLocation() {
    try {
        const params = new URL(window.location.href).searchParams;
        const ai = params.get("ai");
        const pi = params.get("pi");

        return ai ? { ai, pi: pi || "" } : null;
    } catch {
        return null;
    }
}

function remembered() {
    try {
        const raw = sessionStorage.getItem(KEY);

        return raw ? JSON.parse(raw) : null;
    } catch {
        // Private windows, blocked site data, a thumbnail capture. A referral
        // we cannot remember is a referral we do not pass on — never an error
        // in front of a member reading a course.
        return null;
    }
}

/**
 * The referral in force: whatever the URL says, else whatever was stored.
 *
 * The URL wins, so a member who follows a second affiliate's link in the same
 * session promotes the second one — last click, matching what the sales page
 * does with the same two parameters.
 */
export function currentReferral() {
    const fresh = fromLocation();

    if (fresh) {
        try {
            sessionStorage.setItem(KEY, JSON.stringify(fresh));
        } catch {
            // Not fatal: this call still returns the referral, so a buy made
            // from this page carries it even when nothing can be stored.
        }

        return fresh;
    }

    return remembered();
}

/**
 * The same URL, carrying the referral.
 *
 * Returns the URL untouched when there is nothing to add, when it is not an
 * http(s) address, or when it already names an affiliate — a link the creator
 * built with their own `ai` is theirs, and overwriting it would move money.
 */
export function withReferral(url) {
    const referral = currentReferral();

    if (!url || !referral?.ai) {
        return url;
    }

    try {
        const target = new URL(url, window.location.href);

        if (target.protocol !== "http:" && target.protocol !== "https:") {
            return url;
        }

        if (target.searchParams.has("ai")) {
            return url;
        }

        target.searchParams.set("ai", referral.ai);

        if (referral.pi) {
            target.searchParams.set("pi", referral.pi);
        }

        return target.toString();
    } catch {
        // An unparseable URL is handed back as it came. A buy button that
        // works without the referral beats one that throws.
        return url;
    }
}
